import { validateEmailDraft } from '@/lib/flowValidation';
import type { EmailDraft } from '@/lib/flowValidation';
import type { Contact } from '@/types/contacts';
import { fetchWithAuth } from '@/lib/api';

export interface BrandContext {
  senderCompany?: string;
  industry?: string;
  tone?: 'formal' | 'friendly' | 'scrappy' | 'technical' | 'executive';
  productFocus?: string;
}

interface DraftTemplate {
  subject: string;
  body: (name: string, company: string, daysSince: number | null) => string;
  ctaSuggestion: string;
}

const fallbackTemplates: Record<string, DraftTemplate> = {
  new: {
    subject: 'Quick intro — would love to connect',
    body: (name, company) =>
      `Hi ${name},\n\nI came across your profile and thought there could be a great fit with what we're building. We help teams like ${company || 'yours'} move faster on the things that matter.\n\nWould you be open to a quick 15-minute call this week?\n\nBest,\n[Your Name]`,
    ctaSuggestion: 'Schedule intro call',
  },
  qualified: {
    subject: 'Following up — excited to explore further',
    body: (name, _company, daysSince) =>
      `Hi ${name},\n\nFollowing up on our${daysSince ? ` conversation from ${daysSince} days ago` : ' last touchpoint'}. We've been seeing strong results for similar teams and I'd love to share what's resonating.\n\nAre you free for 20 minutes this week?\n\nBest,\n[Your Name]`,
    ctaSuggestion: 'Schedule discovery call',
  },
  proposal: {
    subject: 'Re: Proposal — any questions I can answer?',
    body: (name, _company, daysSince) =>
      `Hi ${name},\n\nJust wanted to check in on the proposal${daysSince ? ` I sent ${daysSince} days ago` : ''}. Happy to walk through anything or adjust the scope to better fit your needs.\n\nLet me know if a quick call would help.\n\nBest,\n[Your Name]`,
    ctaSuggestion: 'Schedule proposal review',
  },
  negotiation: {
    subject: "Let's finalize the details",
    body: (name) =>
      `Hi ${name},\n\nI believe we're close — I wanted to make sure we wrap this up in a way that works well for both sides. Happy to jump on a call to finalize the remaining items.\n\nWhat does your schedule look like this week?\n\nBest,\n[Your Name]`,
    ctaSuggestion: 'Schedule closing call',
  },
  closed_won: {
    subject: 'Welcome — excited to get started',
    body: (name) =>
      `Hi ${name},\n\nSo glad we're working together! I'm attaching the onboarding details and would love to intro you to the team. Let's set up a kickoff call at your earliest convenience.\n\nLooking forward to a great partnership.\n\nBest,\n[Your Name]`,
    ctaSuggestion: 'Schedule kickoff call',
  },
  closed_lost: {
    subject: 'Staying in touch',
    body: (name) =>
      `Hi ${name},\n\nJust wanted to keep the line open — I know timing matters, and things change. If it ever makes sense to revisit, I'm here.\n\nWishing you continued success in the meantime.\n\nBest,\n[Your Name]`,
    ctaSuggestion: 'Keep warm for future',
  },
};

function buildFallback(contact: Contact): EmailDraft | null {
  const template = fallbackTemplates[contact.stage] ?? fallbackTemplates.new;
  const firstName = contact.name.split(' ')[0] ?? contact.name;
  const company = contact.company ?? 'your company';
  const daysSince = contact.lastContactedAt
    ? Math.floor((Date.now() - new Date(contact.lastContactedAt).getTime()) / 86_400_000)
    : null;

  const body = template.body(firstName, company, daysSince);
  const preview = body.split('\n\n')[1]?.slice(0, 120) ?? body.slice(0, 120);

  return validateEmailDraft({
    contactId: contact.id,
    contactName: contact.name,
    contactEmail: contact.email,
    subject: template.subject,
    preview: preview + '…',
    body,
    ctaSuggestion: template.ctaSuggestion,
  });
}

export class EmailDraftingService {
  async generateDraft(
    contact: Contact,
    brandContext?: BrandContext,
    token?: string | null,
  ): Promise<EmailDraft | null> {
    if (token) {
      try {
        const data = await fetchWithAuth(
          '/api/flow/draft-email',
          {
            method: 'POST',
            body: JSON.stringify({ contact, brandContext: brandContext ?? null }),
          },
          token,
        );

        const body = data.body as string;
        const preview = body.split('\n\n')[1]?.slice(0, 120) ?? body.slice(0, 120);

        const draft = validateEmailDraft({
          contactId: contact.id,
          contactName: contact.name,
          contactEmail: contact.email,
          subject: data.subject,
          preview: preview + '…',
          body,
          ctaSuggestion: data.ctaSuggestion,
        });

        if (draft) return draft;
        console.warn('[Flow] LLM draft failed validation — falling back to template');
      } catch (err) {
        console.warn('[Flow] LLM draft error — falling back to template:', err);
      }
    }

    return buildFallback(contact);
  }
}

export const emailDraftingService = new EmailDraftingService();
